/**
 * Thought process preferences: defaults for chain-of-thought (reasoning)
 * and RAG (retrieval). Backed by the Supabase-synced chat preferences store
 * (user_chat_preferences); the legacy "thought_process_preferences"
 * localStorage key was migrated and removed. Public API unchanged.
 */
import {
  getChatPreferencesSnapshot,
  updateChatPreferences,
} from "@/lib/chatPreferencesStore";
import type { UserChatPreferences } from "@shared/chatPreferences";

const RAG_LIMIT_MIN = 3;
const RAG_LIMIT_MAX = 20;
const RAG_THRESHOLD_MIN = 0.2;
const RAG_THRESHOLD_MAX = 0.9;

export interface ThoughtProcessPreferences {
  /** Default thinking mode for new chats (when model supports it). */
  defaultThinkingMode: boolean;
  /** Default RAG on/off for new chats. */
  defaultRagOn: boolean;
  /** Max semantic chunks retrieved per query. */
  ragLimit: number;
  /** Min similarity for a chunk to be included (0–1). */
  ragSimilarityThreshold: number;
  /** Combine explicit (space/topic/docs) + semantic results. */
  ragHybridMode: boolean;
  /** Combine keyword (BM25/FTS) + vector search for retrieval. */
  ragHybridSearch: boolean;
  /** Rerank top candidates with Cohere (requires COHERE_API_KEY). */
  ragUseRerank: boolean;
  /** When true, new/updated documents are automatically embedded into RAG memory. */
  autoEmbedDocuments: boolean;
  /** When true, chat threads are automatically embedded after messages are saved. */
  autoEmbedThreads: boolean;
}

const DEFAULTS: ThoughtProcessPreferences = {
  defaultThinkingMode: false,
  defaultRagOn: false,
  ragLimit: 8,
  ragSimilarityThreshold: 0.45,
  ragHybridMode: true,
  ragHybridSearch: true,
  ragUseRerank: false,
  autoEmbedDocuments: true,
  autoEmbedThreads: true,
};

/**
 * Returns current thought process preferences with defaults applied and values clamped.
 */
export function getThoughtProcessPreferences(): ThoughtProcessPreferences {
  const prefs = getChatPreferencesSnapshot();
  return {
    defaultThinkingMode: prefs.thinkingMode,
    defaultRagOn: prefs.useRag,
    ragLimit: prefs.ragLimit,
    ragSimilarityThreshold: prefs.ragSimilarityThreshold,
    ragHybridMode: prefs.ragHybridMode,
    ragHybridSearch: prefs.ragHybridSearch,
    ragUseRerank: prefs.ragUseRerank,
    autoEmbedDocuments: prefs.autoEmbedDocuments,
    autoEmbedThreads: prefs.autoEmbedThreads,
  };
}

/**
 * Updates thought process preferences. Pass a partial object; only provided fields are updated.
 */
export function setThoughtProcessPreferences(
  partial: Partial<ThoughtProcessPreferences>
): void {
  const update: Partial<UserChatPreferences> = {};
  if (partial.defaultThinkingMode !== undefined) update.thinkingMode = partial.defaultThinkingMode;
  if (partial.defaultRagOn !== undefined) update.useRag = partial.defaultRagOn;
  if (partial.ragLimit !== undefined) update.ragLimit = partial.ragLimit;
  if (partial.ragSimilarityThreshold !== undefined) update.ragSimilarityThreshold = partial.ragSimilarityThreshold;
  if (partial.ragHybridMode !== undefined) update.ragHybridMode = partial.ragHybridMode;
  if (partial.ragHybridSearch !== undefined) update.ragHybridSearch = partial.ragHybridSearch;
  if (partial.ragUseRerank !== undefined) update.ragUseRerank = partial.ragUseRerank;
  if (partial.autoEmbedDocuments !== undefined) update.autoEmbedDocuments = partial.autoEmbedDocuments;
  if (partial.autoEmbedThreads !== undefined) update.autoEmbedThreads = partial.autoEmbedThreads;
  if (Object.keys(update).length > 0) updateChatPreferences(update);
}

export {
  RAG_LIMIT_MIN,
  RAG_LIMIT_MAX,
  RAG_THRESHOLD_MIN,
  RAG_THRESHOLD_MAX,
  DEFAULTS as THOUGHT_PROCESS_DEFAULTS,
};
